<template>
  <div class="job-sync-mapping-tab">
    <div class="page-header">
      <div>
        <h3>Job field mapping</h3>
        <p class="page-subtitle">
          Default two-way mapping for linked ServiceM8 jobs and {{ brand.agencyName }} opportunities.
          Drag pills into a field the same way as auto work orders. Use Create jobs from workflows if a workflow should create a new job.
        </p>
      </div>
      <div class="header-actions">
        <button class="btn-secondary" :disabled="saving || loading" @click="resetToDefaults">
          Reset to defaults
        </button>
        <button class="btn-primary" :disabled="saving || loading || !dirty" @click="saveMappings">
          {{ saving ? 'Saving…' : savedRecently ? 'Saved' : 'Save mapping' }}
        </button>
      </div>
    </div>

    <div v-if="error" class="error-banner">
      <p>{{ error }}</p>
      <button class="btn-secondary" @click="loadMappings">Retry</button>
    </div>

    <div v-else-if="loading" class="loading-state">Loading job field mapping…</div>

    <div v-else class="editor-layout">
      <div class="mapping-column">
        <div class="direction-toggle">
          <button
            :class="['direction-btn', { active: direction === 'ghlToSm8' }]"
            @click="direction = 'ghlToSm8'"
          >
            {{ brand.agencyName }} → ServiceM8
          </button>
          <button
            :class="['direction-btn', { active: direction === 'sm8ToGhl' }]"
            @click="direction = 'sm8ToGhl'"
          >
            ServiceM8 → {{ brand.agencyName }}
          </button>
        </div>

        <div v-if="direction === 'ghlToSm8'" class="job-config-section">
          <h4 class="section-header">ServiceM8 job fields</h4>
          <p class="section-note">
            Applied when a linked {{ brand.agencyName }} opportunity updates the ServiceM8 job. Queue and status still use Queue Mapping and Status Mapping.
          </p>
          <div class="config-field">
            <label class="field-label">Client</label>
            <RichTextFieldInput
              id="ghl-company"
              :value="mappings.ghlToSm8.company"
              :placeholder="'Drag ' + brand.agencyName + ' fields here'"
              min-height="36px"
              :single-line="true"
              @input="(val) => setGhlToSm8('company', val)"
              @blur="scheduleSave"
            />
          </div>
          <div class="config-field">
            <label class="field-label">Job Address</label>
            <RichTextFieldInput
              id="ghl-job-address"
              :value="mappings.ghlToSm8.jobAddress"
              :placeholder="'Drag ' + brand.agencyName + ' fields here'"
              min-height="80px"
              @input="(val) => setGhlToSm8('jobAddress', val)"
              @blur="scheduleSave"
            />
          </div>
          <div class="config-field">
            <label class="field-label">Job Description</label>
            <RichTextFieldInput
              id="ghl-job-description"
              :value="mappings.ghlToSm8.jobDescription"
              placeholder="Enter description or drag fields here"
              min-height="100px"
              @input="(val) => setGhlToSm8('jobDescription', val)"
              @blur="scheduleSave"
            />
          </div>
          <div class="config-field">
            <label class="field-label">PO Number</label>
            <RichTextFieldInput
              id="ghl-po-number"
              :value="mappings.ghlToSm8.poNumber"
              :placeholder="'Drag ' + brand.agencyName + ' fields here'"
              min-height="36px"
              :single-line="true"
              @input="(val) => setGhlToSm8('poNumber', val)"
              @blur="scheduleSave"
            />
          </div>
        </div>

        <div v-else class="job-config-section">
          <h4 class="section-header">{{ brand.agencyName }} opportunity fields</h4>
          <p class="section-note">
            Applied when a ServiceM8 job creates or updates the linked opportunity, including the job iframe Sync action.
          </p>
          <div class="config-field">
            <label class="field-label">Opportunity name</label>
            <RichTextFieldInput
              id="sm8-opp-name"
              :value="mappings.sm8ToGhl.name"
              placeholder="Drag ServiceM8 job fields here"
              min-height="36px"
              :single-line="true"
              @input="(val) => setSm8ToGhl('name', val)"
              @blur="scheduleSave"
            />
          </div>
          <div class="config-field">
            <label class="field-label">Monetary value</label>
            <RichTextFieldInput
              id="sm8-opp-value"
              :value="mappings.sm8ToGhl.monetaryValue"
              placeholder="Drag ServiceM8 job fields here"
              min-height="36px"
              :single-line="true"
              @input="(val) => setSm8ToGhl('monetaryValue', val)"
              @blur="scheduleSave"
            />
          </div>
        </div>
      </div>

      <div class="available-fields-panel">
        <div class="fields-panel-header">
          <h4>{{ direction === 'ghlToSm8' ? brand.agencyName + ' fields' : 'ServiceM8 fields' }}</h4>
          <button
            v-if="direction === 'ghlToSm8'"
            class="btn-refresh-small"
            :disabled="refreshingFields"
            :title="'Refresh custom fields from ' + brand.agencyName"
            @click="loadCustomFields"
          >
            <RefreshCw :size="14" :class="{ loading: refreshingFields }" />
          </button>
        </div>
        <div class="fields-panel-content">
          <div class="fields-search">
            <input
              v-model="fieldsSearchTerm"
              class="fields-search-input"
              type="text"
              placeholder="Search available fields..."
            />
          </div>
          <div class="fields-list">
            <div
              v-for="section in visibleFieldSections"
              :key="section.name"
              class="fields-section"
            >
              <h5 class="fields-section-title">{{ section.name }}</h5>
              <div class="fields-section-items">
                <div
                  v-for="field in section.fields"
                  :key="field.key"
                  class="field-bubble draggable-field"
                  :draggable="true"
                  :data-field-key="field.key"
                  :data-field-display="field.displayName"
                  @dragstart="handleFieldDragStart($event, field.key)"
                  @dragend="handleFieldDragEnd"
                >
                  {{ field.displayName }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import RichTextFieldInput from './RichTextFieldInput.vue';
import { RefreshCw } from 'lucide-vue-next';
import { brandMixin } from '../brandMixin';

const DEFAULT_MAPPINGS = {
  ghlToSm8: {
    company: '{servicem8.client}',
    jobAddress: '{servicem8.jobAddress}',
    jobDescription: '{opportunity.name}',
    poNumber: '',
  },
  sm8ToGhl: {
    name: '{job.generated_job_id} - {job.job_address}',
    monetaryValue: '{job.total_amount}',
  },
};

export default {
  name: 'JobSyncMappingTab',
  mixins: [brandMixin],
  components: { RichTextFieldInput, RefreshCw },
  data() {
    return {
      loading: false,
      saving: false,
      dirty: false,
      savedRecently: false,
      error: null,
      direction: 'ghlToSm8',
      mappings: JSON.parse(JSON.stringify(DEFAULT_MAPPINGS)),
      fieldsSearchTerm: '',
      refreshingFields: false,
      saveTimer: null,
      savedTimer: null,
      customFields: [],
      ghlToSm8Sections: [
        {
          name: 'ServiceM8 formatted fields',
          fields: [
            { key: 'servicem8.client', displayName: 'Client' },
            { key: 'servicem8.jobAddress', displayName: 'Job Address' },
            { key: 'servicem8.mobile', displayName: 'Mobile' },
            { key: 'servicem8.phone', displayName: 'Phone' },
          ],
        },
        {
          name: 'Opportunities',
          fields: [
            { key: 'opportunity.name', displayName: 'Opportunity Name' },
            { key: 'opportunity.status', displayName: 'Status' },
            { key: 'opportunity.monetary_value', displayName: 'Monetary Value' },
            { key: 'opportunity.pipeline_id', displayName: 'Pipeline ID' },
            { key: 'opportunity.pipeline_stage_id', displayName: 'Pipeline Stage ID' },
            { key: 'opportunity.source', displayName: 'Source' },
          ],
        },
        {
          name: 'Contacts',
          fields: [
            { key: 'contact.first_name', displayName: 'First Name' },
            { key: 'contact.last_name', displayName: 'Last Name' },
            { key: 'contact.company_name', displayName: 'Company Name' },
            { key: 'contact.email', displayName: 'Email' },
            { key: 'contact.phone', displayName: 'Phone' },
            { key: 'contact.address1', displayName: 'Address' },
            { key: 'contact.city', displayName: 'City' },
            { key: 'contact.state', displayName: 'State' },
            { key: 'contact.postal_code', displayName: 'Postal Code' },
          ],
        },
      ],
      sm8ToGhlSections: [
        {
          name: 'Job',
          fields: [
            { key: 'job.opportunity_name', displayName: 'Default opportunity name' },
            { key: 'job.generated_job_id', displayName: 'Job number' },
            { key: 'job.uuid', displayName: 'Job UUID' },
            { key: 'job.job_address', displayName: 'Job address' },
            { key: 'job.job_description', displayName: 'Job description' },
            { key: 'job.status', displayName: 'Job status' },
            { key: 'job.purchase_order_number', displayName: 'PO number' },
            { key: 'job.date', displayName: 'Job date' },
            { key: 'job.total_amount', displayName: 'Invoice or quote total' },
            { key: 'job.total_invoice_amount', displayName: 'Invoice total' },
            { key: 'job.quote_total', displayName: 'Quote total' },
          ],
        },
        {
          name: 'Client',
          fields: [{ key: 'company.name', displayName: 'Client name' }],
        },
      ],
    };
  },
  computed: {
    visibleFieldSections() {
      const search = (this.fieldsSearchTerm || '').toLowerCase();
      const base = this.direction === 'ghlToSm8' ? this.ghlToSm8SectionsWithCustom : this.sm8ToGhlSections;
      if (!search) return base;
      return base
        .map((section) => ({
          ...section,
          fields: section.fields.filter((field) => field.displayName.toLowerCase().includes(search) || field.key.toLowerCase().includes(search)),
        }))
        .filter((section) => section.fields.length > 0);
    },
    ghlToSm8SectionsWithCustom() {
      const extra = [];
      const models = [...new Set(this.customFields.map((f) => f.model).filter(Boolean))];
      models.forEach((model) => {
        const fields = this.customFields
          .filter((f) => f.model === model)
          .map((f) => ({
            key: `${model}.${f.id}`,
            displayName: f.name || f.fieldKey || f.id,
          }));
        if (fields.length) {
          extra.push({
            name: `${model.charAt(0).toUpperCase() + model.slice(1)} Custom Fields`,
            fields,
          });
        }
      });
      return [...this.ghlToSm8Sections, ...extra];
    },
  },
  mounted() {
    this.loadMappings();
    this.loadCustomFields();
  },
  beforeUnmount() {
    if (this.saveTimer) clearTimeout(this.saveTimer);
    if (this.savedTimer) clearTimeout(this.savedTimer);
  },
  methods: {
    async getAuthHeaders() {
      const addonSession = new URLSearchParams(window.location.search).get('addon_session');
      if (addonSession) {
        return { 'x-addon-session': addonSession, 'x-sso-token': addonSession };
      }
      const ssoToken = await new Promise((resolve) => {
        window.parent.postMessage({ message: 'REQUEST_USER_DATA' }, '*');
        const handler = ({ data }) => {
          if (data.message === 'REQUEST_USER_DATA_RESPONSE') {
            window.removeEventListener('message', handler);
            resolve(data.payload);
          }
        };
        window.addEventListener('message', handler);
      });
      return { 'x-sso-token': ssoToken };
    },
    async loadMappings() {
      this.loading = true;
      this.error = null;
      try {
        const headers = await this.getAuthHeaders();
        const response = await fetch('/servicem8/job-sync-field-mappings', { headers });
        if (!response.ok) throw new Error('Failed to load job field mapping');
        const data = await response.json();
        this.mappings = {
          ghlToSm8: { ...DEFAULT_MAPPINGS.ghlToSm8, ...(data.mappings?.ghlToSm8 || {}) },
          sm8ToGhl: { ...DEFAULT_MAPPINGS.sm8ToGhl, ...(data.mappings?.sm8ToGhl || {}) },
        };
        this.dirty = false;
      } catch (error) {
        this.error = error.message || 'Failed to load job field mapping';
      } finally {
        this.loading = false;
      }
    },
    async loadCustomFields() {
      this.refreshingFields = true;
      try {
        const headers = await this.getAuthHeaders();
        const response = await fetch('/ghl/custom-fields', { headers });
        if (response.ok) {
          const data = await response.json();
          this.customFields = data.customFields || [];
        }
      } catch (error) {
        console.error('Error loading custom fields:', error);
      } finally {
        this.refreshingFields = false;
      }
    },
    setGhlToSm8(key, value) {
      this.mappings.ghlToSm8[key] = typeof value === 'string' ? value : '';
      this.dirty = true;
      this.scheduleSave();
    },
    setSm8ToGhl(key, value) {
      this.mappings.sm8ToGhl[key] = typeof value === 'string' ? value : '';
      this.dirty = true;
      this.scheduleSave();
    },
    scheduleSave() {
      this.dirty = true;
      if (this.saveTimer) clearTimeout(this.saveTimer);
      this.saveTimer = setTimeout(() => this.saveMappings(), 700);
    },
    async saveMappings() {
      this.saving = true;
      this.error = null;
      try {
        const headers = await this.getAuthHeaders();
        const response = await fetch('/servicem8/job-sync-field-mappings', {
          method: 'POST',
          headers: { ...headers, 'Content-Type': 'application/json' },
          body: JSON.stringify({ mappings: this.mappings }),
        });
        if (!response.ok) throw new Error('Failed to save job field mapping');
        const data = await response.json();
        if (data.mappings) {
          this.mappings = {
            ghlToSm8: { ...DEFAULT_MAPPINGS.ghlToSm8, ...(data.mappings.ghlToSm8 || {}) },
            sm8ToGhl: { ...DEFAULT_MAPPINGS.sm8ToGhl, ...(data.mappings.sm8ToGhl || {}) },
          };
        }
        this.dirty = false;
        this.savedRecently = true;
        if (this.savedTimer) clearTimeout(this.savedTimer);
        this.savedTimer = setTimeout(() => {
          this.savedRecently = false;
        }, 1600);
      } catch (error) {
        this.error = error.message || 'Failed to save job field mapping';
      } finally {
        this.saving = false;
      }
    },
    async resetToDefaults() {
      if (!confirm('Reset job field mapping to the standard two-way defaults?')) return;
      this.saving = true;
      this.error = null;
      try {
        const headers = await this.getAuthHeaders();
        const response = await fetch('/servicem8/job-sync-field-mappings/reset', {
          method: 'POST',
          headers,
        });
        if (!response.ok) throw new Error('Failed to reset job field mapping');
        const data = await response.json();
        this.mappings = {
          ghlToSm8: { ...DEFAULT_MAPPINGS.ghlToSm8, ...(data.mappings?.ghlToSm8 || {}) },
          sm8ToGhl: { ...DEFAULT_MAPPINGS.sm8ToGhl, ...(data.mappings?.sm8ToGhl || {}) },
        };
        this.dirty = false;
      } catch (error) {
        this.error = error.message || 'Failed to reset job field mapping';
      } finally {
        this.saving = false;
      }
    },
    handleFieldDragStart(event, fieldKey) {
      try {
        event.dataTransfer.setData('text/plain', `{${fieldKey}}`);
        event.dataTransfer.setData('application/merge-field', fieldKey);
        event.dataTransfer.effectAllowed = 'copy';
      } catch (e) {
        console.error('Error setting drag data:', e);
      }
      event.target.style.opacity = '0.5';
      event.target.classList.add('dragging');
    },
    handleFieldDragEnd(event) {
      event.target.style.opacity = '1';
      event.target.classList.remove('dragging');
    },
  },
};
</script>

<style scoped>
.job-sync-mapping-tab {
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 24px;
  background: #f1f5f9;
  min-height: calc(100vh - 64px);
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
}

.page-header h3 {
  margin: 0 0 6px 0;
  font-size: 20px;
  font-weight: 700;
  color: #0f172a;
}

.page-subtitle {
  margin: 0;
  max-width: 720px;
  color: #64748b;
  font-size: 14px;
  line-height: 1.5;
}

.header-actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}

.btn-primary,
.btn-secondary {
  padding: 8px 14px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.btn-primary {
  background: linear-gradient(135deg, rgb(0, 157, 255) 0%, rgb(0, 227, 255) 100%);
  border: 1px solid #24acfe;
  color: white;
}

.btn-primary:disabled,
.btn-secondary:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.btn-secondary {
  background: white;
  border: 1px solid #cbd5e1;
  color: #334155;
}

.loading-state,
.error-banner {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px 20px;
  color: #334155;
}

.error-banner {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  color: #b91c1c;
}

.editor-layout {
  display: flex;
  align-items: flex-start;
  gap: 24px;
}

.mapping-column {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.direction-toggle {
  display: inline-flex;
  background: white;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  padding: 4px;
  width: fit-content;
}

.direction-btn {
  border: none;
  background: transparent;
  padding: 8px 14px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #64748b;
  cursor: pointer;
}

.direction-btn.active {
  background: #e0f2fe;
  color: #0369a1;
}

.job-config-section {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.section-header {
  margin: 0;
  font-size: 17px;
  font-weight: 700;
  color: #1e293b;
}

.section-note {
  margin: -8px 0 0 0;
  font-size: 13px;
  color: #64748b;
  line-height: 1.45;
}

.config-field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.field-label {
  font-size: 14px;
  font-weight: 600;
  color: #334155;
}

.available-fields-panel {
  position: sticky;
  top: 20px;
  width: 340px;
  max-height: calc(100vh - 40px);
  background: white;
  border: 1px solid #cbd5e1;
  border-radius: 12px;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  flex-shrink: 0;
}

.fields-panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #e2e8f0;
  background: #f8fafc;
}

.fields-panel-header h4 {
  margin: 0;
  font-size: 15px;
  font-weight: 700;
  color: #1e293b;
}

.btn-refresh-small {
  padding: 6px;
  background: white;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-refresh-small .loading {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.fields-panel-content {
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

.fields-search {
  padding: 12px 16px;
  border-bottom: 1px solid #e2e8f0;
}

.fields-search-input {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-size: 13px;
  outline: none;
}

.fields-search-input:focus {
  border-color: #24acfe;
  box-shadow: 0 0 0 3px rgba(36, 172, 254, 0.1);
}

.fields-list {
  padding: 12px 16px;
}

.fields-section {
  margin-bottom: 20px;
}

.fields-section-title {
  margin: 0 0 10px 0;
  font-size: 11px;
  font-weight: 700;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.fields-section-items {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.field-bubble {
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  background: white;
  border: 1px solid #24acfe;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #24acfe;
  cursor: grab;
  user-select: none;
}

.field-bubble:hover {
  background: #f0f9ff;
}

.field-bubble.dragging {
  opacity: 0.5;
}

@media (max-width: 960px) {
  .editor-layout {
    flex-direction: column;
  }

  .available-fields-panel {
    position: static;
    width: 100%;
    max-height: 420px;
  }

  .page-header {
    flex-direction: column;
  }
}
</style>
